import { useMemo } from 'react';
import useSWR from 'swr';
import type { SWRConfiguration } from 'swr';

import { fetcher, endpoints } from '@/lib/axios';
import type {
  IAvis,
  ITestimonial,
  IPaginationMeta,
  IReviewsResponse,
  ITestimonialsResponse,
} from '@/types/testimonial';

// ----------------------------------------------------------------------

const swrOptions: SWRConfiguration = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  shouldRetryOnError: false,
};

// ----------------------------------------------------------------------
// Approved reviews — GET /api/v1/reviews (paginated, server-side)
// ----------------------------------------------------------------------

export function useGetReviews(page: number | null = 1) {
  const { data, isLoading, error, isValidating } = useSWR<IReviewsResponse>(
    // Pass `null` to skip the request entirely (e.g. when reviews are supplied
    // from elsewhere, like a formation detail payload).
    page ? endpoints.reviews.list(page) : null,
    fetcher,
    { ...swrOptions, keepPreviousData: true },
  );

  return useMemo(
    () => ({
      reviews: (data?.data ?? []) as IAvis[],
      reviewsMeta: data?.meta as IPaginationMeta | undefined,
      reviewsLoading: isLoading,
      reviewsError: error as Error | undefined,
      reviewsValidating: isValidating,
      reviewsEmpty: !isLoading && !(data?.data?.length),
    }),
    [data, error, isLoading, isValidating],
  );
}

// ----------------------------------------------------------------------
// Published testimonials — GET /api/v1/testimonials
// ----------------------------------------------------------------------

export function useGetTestimonials() {
  const { data, isLoading, error, isValidating } = useSWR<ITestimonialsResponse>(
    endpoints.testimonials.list,
    fetcher,
    swrOptions,
  );

  return useMemo(
    () => ({
      testimonials: (data?.data ?? []) as ITestimonial[],
      testimonialsLoading: isLoading,
      testimonialsError: error as Error | undefined,
      testimonialsValidating: isValidating,
      testimonialsEmpty: !isLoading && !(data?.data?.length),
    }),
    [data, error, isLoading, isValidating],
  );
}
